import { useMemo } from "react";

import { useTimezone } from "@/hooks/use-timezone";

import {
  deduplicateValuesByDate,
  groupValuesByDate,
  resolveGroupFieldIds,
} from "@/lib/measurement-utils";

import type {
  MeasurementField,
  MeasurementGroupWithFields,
  MeasurementValueSummary,
} from "@/types/measurement";

interface GroupAssignment {
  fieldIds: Set<string>;
  groupIds: Set<string>;
}

function resolveGroupAndChildGroupIds(
  group: MeasurementGroupWithFields,
  visited = new Set<string>(),
): Set<string> {
  const groupIds = new Set<string>();
  if (visited.has(group.id)) return groupIds;
  visited.add(group.id);
  groupIds.add(group.id);

  for (const membership of group.fieldMemberships) {
    if (!membership.childGroup) continue;
    resolveGroupAndChildGroupIds(membership.childGroup, visited).forEach((id) =>
      groupIds.add(id),
    );
  }

  return groupIds;
}

function buildGroupAssignment(
  group: MeasurementGroupWithFields,
): GroupAssignment {
  return {
    fieldIds: resolveGroupFieldIds(group),
    groupIds: resolveGroupAndChildGroupIds(group),
  };
}

function valueBelongsToGroup(
  value: MeasurementValueSummary,
  assignment: GroupAssignment,
): boolean {
  if (!assignment.fieldIds.has(value.fieldId)) return false;

  const groupIds = value.groupIds ?? [];
  if (groupIds.length > 0) {
    return groupIds.some((groupId) => assignment.groupIds.has(groupId));
  }

  return true;
}

function valueIsAssignedToCurrentGroup(
  value: MeasurementValueSummary,
  groupAssignments: GroupAssignment[],
): boolean {
  return groupAssignments.some((assignment) =>
    valueBelongsToGroup(value, assignment),
  );
}

export function useGroupedMeasurementData(
  groups: MeasurementGroupWithFields[] | undefined,
  fields: MeasurementField[] | undefined,
  allValues: MeasurementValueSummary[] | undefined,
) {
  const timezone = useTimezone();
  const { groupedData, rawGroupedData, groupsWithData, allGroupedFieldIds } =
    useMemo(() => {
      if (!groups || !allValues)
        return {
          groupedData: {} as Record<
            string,
            Record<string, MeasurementValueSummary[]>
          >,
          rawGroupedData: {} as Record<
            string,
            Record<string, MeasurementValueSummary[]>
          >,
          groupsWithData: [] as MeasurementGroupWithFields[],
          allGroupedFieldIds: new Set<string>(),
        };

      const data: Record<
        string,
        Record<string, MeasurementValueSummary[]>
      > = {};
      const rawData: Record<
        string,
        Record<string, MeasurementValueSummary[]>
      > = {};
      const allFieldIds = new Set<string>();

      for (const group of groups) {
        const assignment = buildGroupAssignment(group);
        assignment.fieldIds.forEach((id) => allFieldIds.add(id));
        const groupValues = allValues.filter((value) =>
          valueBelongsToGroup(value, assignment),
        );
        const groupedValues = groupValuesByDate(groupValues, timezone);
        rawData[group.id] = groupedValues;
        data[group.id] = deduplicateValuesByDate(groupedValues);
      }

      const withData = groups.filter(
        (g) => Object.keys(data[g.id] ?? {}).length > 0,
      );

      return {
        groupedData: data,
        rawGroupedData: rawData,
        groupsWithData: withData,
        allGroupedFieldIds: allFieldIds,
      };
    }, [groups, allValues, timezone]);

  const { otherGroup, otherValuesByDate, rawOtherValuesByDate } =
    useMemo(() => {
      if (!allValues || !fields)
        return {
          otherGroup: null,
          otherValuesByDate: {} as Record<string, MeasurementValueSummary[]>,
          rawOtherValuesByDate: {} as Record<string, MeasurementValueSummary[]>,
        };

      const groupAssignments = (groups ?? []).map(buildGroupAssignment);
      const ungrouped = allValues.filter(
        (value) => !valueIsAssignedToCurrentGroup(value, groupAssignments),
      );

      if (ungrouped.length === 0)
        return {
          otherGroup: null,
          otherValuesByDate: {},
          rawOtherValuesByDate: {},
        };

      const rawByDate = groupValuesByDate(ungrouped, timezone);
      const byDate = deduplicateValuesByDate(rawByDate);

      const ungroupedFieldIds = new Set(ungrouped.map((v) => v.fieldId));
      const fieldMap = new Map(fields.map((f) => [f.id, f]));

      const memberships = Array.from(ungroupedFieldIds).map((fId, idx) => {
        const f = fieldMap.get(fId) ?? null;
        return {
          id: `other-membership-${fId}`,
          groupId: "",
          fieldId: fId,
          childGroupId: null,
          sortOrder: idx,
          isRequired: false,
          field: f,
          childGroup: null,
        };
      });

      const syntheticGroup: MeasurementGroupWithFields = {
        id: "",
        name: "Other Measurements",
        createdDate: "",
        isArchived: false,
        description: null,
        color: null,
        sortOrder: 999,
        displayConfig: null,
        fieldMemberships: memberships,
      };

      return {
        otherGroup: syntheticGroup,
        otherValuesByDate: byDate,
        rawOtherValuesByDate: rawByDate,
      };
    }, [allValues, fields, groups, timezone]);

  return {
    groupedData,
    rawGroupedData,
    groupsWithData,
    allGroupedFieldIds,
    otherGroup,
    otherValuesByDate,
    rawOtherValuesByDate,
  };
}
